import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { UserStatus } from "Infrastructure/Api/Api";
import type { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Navigate } from "Navigation/Navigate";
import { useSearchParams } from "Navigation/useSearchParams";
import { useEffect } from "react";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { setLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  roleIntent: typeof AccessRoleCodes.Hunter | typeof AccessRoleCodes.Merchant;
};

/**
 * Compatibility shim for retired role-specific registration URLs (/sign-up-merchant and
 * /sign-up-hunter) still circulating in e-mails and shared links. Registration is
 * Customer-first: anonymous visitors continue to /sign-up carrying the role as an intent;
 * signed-in users go straight to the add-role picker.
 */
export const RoleSignUpRedirect = ({ roleIntent }: Props) => {
  const [searchParams] = useSearchParams();
  const currentUser = useLoggedInUser();

  const affiliateCode = searchParams.get("affiliateCode");
  const isSignedIn =
    !!currentUser && currentUser.userStatus !== UserStatus.Anonymous;

  useEffect(() => {
    // The add-role forms read the code from storage — keep it for the signed-in shortcut.
    if (isSignedIn && affiliateCode) {
      setLocalStorageItem(LocalStorageKeys.AffiliateCode, affiliateCode);
    }
  }, [isSignedIn, affiliateCode]);

  if (isSignedIn) {
    return <Navigate to={appUrl("add-role")} replace />;
  }

  const params = new URLSearchParams(searchParams);
  params.set("roleIntent", roleIntent);

  return <Navigate to={`${appUrl("sign-up")}?${params.toString()}`} replace />;
};
